import React, { createContext, ReactNode, useContext, useMemo } from 'react';

import { LabelsContext } from 'contextProviders/SplashScreenProviders/ContentProviders/LabelsProvider';
import { V3AppsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import usePopupReports from 'contextProviders/SplashScreenProviders/ContentDependantProviders/PopupReportUsageLookupProvider/usePopupReports';
import BaseViewsContext from 'contexts/BaseViewsContext';
import BoardsContext from 'contexts/BoardsContext';
import DashboardGadgetsContext from 'contexts/DashboardGadgetsContext';
import DashboardsContext from 'contexts/DashboardsContext';
import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import GoalsContext from 'contexts/GoalsContext';
import LegacySavedFiltersContext from 'contexts/LegacySavedFiltersContext';
import MetricListsContext from 'contexts/MetricListsContext';
import ReportGadgetsContext from 'contexts/ReportGadgetsContext';
import ReportsContext from 'contexts/ReportsContext';
import ScorecardsContext from 'contexts/ScorecardsContext';
import SavedFiltersContext from 'screens/DataManager/DatasetFilters/SavedFilters/context/SavedFiltersContext';
import useGetMetricIdsFromVis from 'hooks/useGetMetricIdsFromVis';
import useKbDomains from 'hooks/useKbDomains';
import useMetricOptions from 'hooks/useMetricOptions';
import useToFixedFilters from 'hooks/useToFixedFilters';

import analyseReport from './analyseReport';
import buildCoverage from './buildCoverage';
import buildReportUsageIndex, { EMPTY_USAGE } from './buildReportUsageIndex';
import buildSuggestions from './buildSuggestions';
import collectInUseMetrics from './collectInUseMetrics';

// The whole analysis, computed once.
//
// A provider rather than a hook on purpose: the coverage header, the roster and
// the suggestion list all read the same derivation, and three calls to a bare
// hook would run it three times over every report and dashboard in the account.

const EMPTY_ANALYSIS: AiReady.Analysis = {
  analysed: [],
  shippedDomains: [],
  aiReady: [],
  coverage: {
    reachableMetricIds: [],
    ambiguousMetricIds: [],
    clusters: [],
    byDomain: [],
  },
  suggestions: [],
};

export const AiReadyContext = createContext<AiReady.Analysis>(EMPTY_ANALYSIS);

const AiReadyProvider = ({ children }: { children: ReactNode }) => {
  const { allReports, archivedReports } = useContext(ReportsContext);
  const { allDashboards } = useContext(DashboardsContext);
  const { boards } = useContext(BoardsContext);
  const { scorecards, scorecardsLookup } = useContext(ScorecardsContext);
  const { goals } = useContext(GoalsContext);
  const { metricListsLookup } = useContext(MetricListsContext);
  const { reportGadgetsLookup } = useContext(ReportGadgetsContext);
  const { dashboardGadgetsLookup } = useContext(DashboardGadgetsContext);
  const { baseViews } = useContext(BaseViewsContext);
  const { savedFilterIdLookup } = useContext(SavedFiltersContext);
  const { savedFiltersLookup } = useContext(LegacySavedFiltersContext);
  const { allProductApps, allWorkspaces } = useContext(V3AppsContext);
  const { entityDetailsApps } = useContext(EntityDefinitionsContext);
  const { labelLookup } = useContext(LabelsContext);

  const { metricOptionsLookup } = useMetricOptions();
  const getMetricIds = useGetMetricIdsFromVis();
  const toFixedFilters = useToFixedFilters();
  const { domains: shippedDomains, domainGroups } = useKbDomains();
  const popupReports = usePopupReports({ onlyFromPublishedSources: false });

  const value = useMemo<AiReady.Analysis>(() => {
    const lookups = {
      dashboardGadgetsLookup,
      scorecardsLookup,
      metricListsLookup,
      reportGadgetsLookup,
      goals,
      getMetricIds,
    };

    // Hidden reports are in archivedReports, not allReports — an AI-ready
    // report someone deleted is still in the agent roster, so leaving it out
    // would make the worst finding on this screen undetectable.
    const everyReport = [
      ...allReports.map((report) => ({ report, isHidden: false })),
      ...archivedReports.map((report) => ({ report, isHidden: true })),
    ];

    const usageIndex = buildReportUsageIndex({
      apps: [...allProductApps, ...allWorkspaces],
      entityApps: entityDetailsApps,
      popupReportIds: popupReports.flatMap(({ report, usage }) =>
        usage.map(() => report.id),
      ),
      reports: everyReport.map((entry) => entry.report),
      labelLookup,
    });

    const analysed = everyReport.map(({ report, isHidden }) =>
      analyseReport({
        report,
        isHidden,
        reportGadgetsLookup,
        baseViews,
        metricOptionsLookup,
        savedFilterIdLookup,
        legacySavedFiltersLookup: savedFiltersLookup,
        toFixedFilters,
        getMetricIds,
        usage: usageIndex[report.id] ?? EMPTY_USAGE,
        shippedDomains,
      }),
    );

    const inUseIndex = collectInUseMetrics({
      dashboards: allDashboards,
      reports: allReports,
      boards,
      scorecards,
      goals,
      lookups,
    });

    const coverage = buildCoverage({
      analysed,
      metricOptionsLookup,
      domainGroups,
    });

    return {
      analysed,
      shippedDomains,
      aiReady: analysed
        .filter((report) => report.isAiReady)
        .sort((a, b) => a.reportName.localeCompare(b.reportName)),
      coverage,
      suggestions: buildSuggestions({
        analysed,
        inUseIndex,
        metricOptionsLookup,
        coverage,
      }),
    };
  }, [
    allDashboards,
    allProductApps,
    allReports,
    allWorkspaces,
    archivedReports,
    baseViews,
    boards,
    dashboardGadgetsLookup,
    domainGroups,
    entityDetailsApps,
    getMetricIds,
    goals,
    labelLookup,
    metricListsLookup,
    metricOptionsLookup,
    popupReports,
    reportGadgetsLookup,
    savedFilterIdLookup,
    savedFiltersLookup,
    scorecards,
    scorecardsLookup,
    shippedDomains,
    toFixedFilters,
  ]);

  return (
    <AiReadyContext.Provider value={value}>{children}</AiReadyContext.Provider>
  );
};

export default AiReadyProvider;
